import React, { useState } from "react";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { FlameIconSvg } from "../../../svgFiles/FlameIconSvg";
import { WashIconSvg } from "../../../svgFiles/WashIconSvg";
import { RipeningIconSvg } from "../../../svgFiles/RipeningIconSvg";
import {
  useGetReworkAreaSummaryQuery,
  useGetReworkPackingSummaryQuery,
  useGetToastedSummaryQuery,
} from "../rework/reworkApi";
import { useGetHumidSummaryQuery } from "../humid/humidApi";
import { useGetGreenRoomSummaryQuery } from "../greenroom/greenRoomApi";

const MetricBox = ({ label, value, unit }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value">
      {value ?? "0"}
      {unit && <span className="db-metric-unit"> {unit}</span>}
    </div>
  </div>
);

const ReworkDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [, setStartDate] = useState(currentDate);
  const [, setEndDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
    setLotNumber(makePriorLotNumber(start));
  };

  const { data: reworkSummaryData } = useGetReworkAreaSummaryQuery({
    tab: "rework",
    date,
    lot_number: lotNumber,
  });
  const { data: reworkPackingSummaryData } = useGetReworkPackingSummaryQuery({
    date,
    lot_number: lotNumber,
  });
  const { data: toastedSummaryData } = useGetToastedSummaryQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );
  const { data: humidSummaryData } = useGetHumidSummaryQuery({ date });
  const { data: greenRoomSummaryData } = useGetGreenRoomSummaryQuery({ date });

  // Rework Area Items
  const reworkItems = [
    {
      label: _t("total_prime_kg", "Total Prime KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg || 0,
      unit: " KG",
    },
    {
      label: _t("prime_cheeks_kg", "Prime Cheeks KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg_in_cheeks || 0,
      unit: " KG",
    },
    {
      label: _t("prime_strips_kg", "Prime Strips KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg_in_strips || 0,
      unit: " KG",
    },
    {
      label: _t("rejected_kg", "Rejected KG"),
      value: reworkSummaryData?.data?.total_no_of_trash_kg || 0,
      unit: " KG",
    },
    {
      label: _t("seconds_cheeks_kg", "Seconds Cheeks KG"),
      value: reworkSummaryData?.data?.seconds_cheeks_kg || 0,
      unit: " KG",
    },
    {
      label: _t("seconds_strips_kg", "Seconds Strips KG"),
      value: reworkSummaryData?.data?.seconds_strips_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_tapita_kg", "Total Tapita KG"),
      value: reworkSummaryData?.data?.total_tapita_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_pieces_kg", "Total Pieces KG"),
      value: reworkSummaryData?.data?.total_pieces_kg || 0,
      unit: " KG",
    },
  ];

  // Rework Packing Items
  const reworkPackingItems = [
    {
      label: _t("total_boxes", "Total Boxes"),
      value: reworkPackingSummaryData?.data?.total_boxes_packed || 0,
    },
    {
      label: _t("prime_cheek_boxes_10_pound", "Prime Cheeks (10 LB)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_cheek_boxes_10_pound || 0,
    },
    {
      label: _t("prime_cheek_boxes_20_pound", "Prime Cheeks (20 LB)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("prime_strip_boxes_20_pound", "Prime Strips (20 LB)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_strip_boxes_20_pound || 0,
    },
  ];

  // Toasted Items
  const toastedItems = [
    {
      label: _t("total_toasted_cheeks_kg", "Total Toasted Cheeks KG"),
      value: toastedSummaryData?.data?.total_kg_cheeks_in_toasted || 0,
      unit: " KG",
    },
    {
      label: _t("total_toasted_strips_kg", "Total Toasted Strips KG"),
      value: toastedSummaryData?.data?.total_kg_strips_in_toasted || 0,
      unit: " KG",
    },
    {
      label: _t("total_cheeks_kg_bagged", "Total Cheeks KG Bagged"),
      value: toastedSummaryData?.data?.total_cheeks_kg_bagged || 0,
      unit: " KG",
    },
    {
      label: _t("total_strips_kg_bagged", "Total Strips KG Bagged"),
      value: toastedSummaryData?.data?.total_strips_kg_bagged || 0,
      unit: " KG",
    },
  ];

  // Humid Items
  const humidItems = [
    {
      label: _t("total_humid_cheeks_kg", "Total Humid Cheeks KG"),
      value: humidSummaryData?.data?.total_kg_cheeks_in_humidity || 0,
      unit: " KG",
    },
    {
      label: _t("total_humid_strips_kg", "Total Humid Strips KG"),
      value: humidSummaryData?.data?.total_kg_strips_in_humidity || 0,
      unit: " KG",
    },
    {
      label: _t("total_carts_in_oven", "Total Carts in Oven"),
      value: humidSummaryData?.data?.total_carts_in_ovens || 0,
    },
    {
      label: _t("carts_in_cooling_room", "Carts in Cooling Room"),
      value: humidSummaryData?.data?.total_carts_in_cooling_room || 0,
    },
    {
      label: _t("total_cheeks_kg_bagged", "Total Cheeks KG Bagged"),
      value: humidSummaryData?.data?.total_cheeks_kg_bagged || 0,
      unit: " KG",
    },
    {
      label: _t("total_strips_kg_bagged", "Total Strips KG Bagged"),
      value: humidSummaryData?.data?.total_strips_kg_bagged || 0,
      unit: " KG",
    },
    {
      label: _t("evaporation_prcnt", "Evaporation %"),
      value: humidSummaryData?.data?.evaporation_prcnt || 0,
    },
  ];

  // Green Room Items
  const greenRoomItems = [
    {
      label: _t("total_green_room_cheeks_kg", "Total Green Room Cheeks KG"),
      value: greenRoomSummaryData?.data?.total_kg_cheeks_in_green_room || 0,
      unit: " KG",
    },
    {
      label: _t("total_green_room_strips_kg", "Total Green Room Strips KG"),
      value: greenRoomSummaryData?.data?.total_kg_strips_in_green_room || 0,
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("rework_overview_title", "Rework Overview")}
        </h5>
        <div className="receiving_filter_group">
          <DateRangeFilter
            setDate={setDate}
            onApplyRange={handleApplyRange}
            triggerId="rework-date-range-filter"
          />
        </div>
      </div>

      <div>
        {/* ── Rework Area Section (2 rows in accordion) ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <ScaleIconSvg size={16} color="#ee7225" />
              {_t("rework_area_overview", "Rework Area Overview")}
            </span>
          }
        >
          <div className="db-evap-row p-0 mb-3">
            {reworkItems.slice(0, 4).map((item, i) => (
              <MetricBox
                key={i}
                label={item.label}
                value={item.value}
                unit={item.unit}
              />
            ))}
          </div>
          <div className="db-evap-row p-0">
            {reworkItems.slice(4).map((item, i) => (
              <MetricBox
                key={i}
                label={item.label}
                value={item.value}
                unit={item.unit}
              />
            ))}
          </div>
        </DashboardSection>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* ── Rework Packing Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <PackageIconSvg size={16} color="#ee7225" />
                {_t("rework_packing_overview", "Rework Packing Overview")}
              </span>
            }
          >
            <div className="db-evap-row p-0">
              {reworkPackingItems.map((item, i) => (
                <MetricBox
                  key={i}
                  label={item.label}
                  value={item.value}
                  unit={item.unit}
                />
              ))}
            </div>
          </DashboardSection>

          {/* ── Humidity Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <WashIconSvg size={16} color="#ee7225" />
                {_t("humidity_room_overview", "Humidity Room Overview")}
              </span>
            }
          >
            <div className="db-evap-row p-0 mb-3">
              {humidItems.slice(0, 4).map((item, i) => (
                <MetricBox
                  key={i}
                  label={item.label}
                  value={item.value}
                  unit={item.unit}
                />
              ))}
            </div>
            <div className="db-evap-row p-0">
              {humidItems.slice(4).map((item, i) => (
                <MetricBox
                  key={i}
                  label={item.label}
                  value={item.value}
                  unit={item.unit}
                />
              ))}
            </div>
          </DashboardSection>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* ── Toasted Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <FlameIconSvg size={16} color="#ee7225" />
                {_t("toasted_room_overview", "Toasted Room Overview")}
              </span>
            }
          >
            <div className="db-trash-grid p-0">
              {toastedItems.map((item, i) => (
                <div key={i} className="db-trash-cell">
                  <span>{item.label}</span>
                  <span className="db-trash-val neutral">
                    {item.value ?? "0"}
                    {item.unit && (
                      <span className="db-metric-unit"> {item.unit}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </DashboardSection>

          {/* ── Green Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                <RipeningIconSvg size={16} color="#ee7225" />
                {_t("green_room_overview", "Green Room Overview")}
              </span>
            }
          >
            <div className="db-evap-row p-0">
              {greenRoomItems.map((item, i) => (
                <MetricBox
                  key={i}
                  label={item.label}
                  value={item.value}
                  unit={item.unit}
                />
              ))}
            </div>
          </DashboardSection>
        </div>
      </div>
    </div>
  );
};

export default ReworkDashboard;
